import React from 'react';
import SortTable from '../SortTable';
import ListResults from '../ListResults';
import shallowequal from 'shallowequal';
import Icon from '../Icon';
import {Link} from 'react-router';
import svgEdit from '../../svgs/edit.svg';
import filter from "../../filter";
import {executeBackendQuery} from "../../requests/executeBackendQuery";
import {VENDOR_LIST, VENDOR_LIST_COUNT} from '../../consts/queries';
import LinkComp from "../LinkComp";
import plus from "../../svgs/plus-white.svg";
import OpenInCrmButton from "../OpenInCrmButton/OpenInCrmButton";
import FilterButton from "../FilterButton";
import FindDuplicateContacts from "../FindDuplicateContacts";
import fetchFilteredIds from "../FindDuplicateContacts/fetchFilteredIds";
import {buildServerQuery, applyClientSort} from "../../sort-utils";
import Alert from "../Alert";
import {can} from "../../access";
import startContactMailMerge from "../MailMerge/startContactMailMerge";
import {AUDIENCE_VENDORS} from "../MailMerge/merge";
import {formatStoredPhoneNumber} from "../../utils";

const limit = 50;

function phoneCell(value) {
    if (!value) return null;
    const digits = String(value).replace(/\D/g, '');
    const display = formatStoredPhoneNumber(value);
    return digits.length >= 10 ? <a href={`tel:${digits}`}>{display}</a> : display;
}

export default class VendorList extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            items: null,
            totalCount: null,
            error: null,
            isBusy: null,
            findDupsOpen: false,
            preparingMailMerge: false,
            mailMergeError: null,
        };
        this.startMailMerge = this.startMailMerge.bind(this);
    }

    /** Every vendor the filter matches, across every page, into the mail merge. */
    startMailMerge() {
        this.setState({preparingMailMerge: true, mailMergeError: null});

        startContactMailMerge({
            audience: AUDIENCE_VENDORS,
            noun: 'vendor',
            queryName: VENDOR_LIST,
            filterKey: 'vendor',
            query: this.props.location.query,
        }).catch((err) => this.setState({
            preparingMailMerge: false,
            mailMergeError: err.message || 'Could not read this list.',
        }));
    }

    componentDidMount() {
        this.fetch(this.props.location.query);
    }

    fetch(q) {
        this.setState({isBusy: true});

        const query = {...q};
        const options = filter(buildServerQuery(query), 'vendor');
        options.param.limit = limit;
        options.param.offset = query.page ? ((Number(query.page) - 1) * limit) : 0;

        Promise.all([
            executeBackendQuery({queryName: VENDOR_LIST, options: options}),
            executeBackendQuery({queryName: VENDOR_LIST_COUNT, options: options}),
        ]).then(res => {
            const activeMembers = (res[0].data || []).filter(item => item.DELETED !== '1');
            this.setState({
                items: applyClientSort(activeMembers, query.order),
                totalCount: res[1].success && res[1].data[0] ? Number(res[1].data[0].TOTAL_COUNT) : false,
                error: res[0].success ? null : res[0].message,
                isBusy: null,
            });
        });
    }

    componentDidUpdate(prevProps) {
        if (!shallowequal(prevProps.location.query, this.props.location.query)) {
            this.fetch(this.props.location.query);
        }
    }

    render() {
        const query = this.props.location.query;

        const columns = [
            // Edit column temporarily disabled
            // {
            //     name: '',
            //     textAlign: 'center',
            //     columnStyle: {width: 70},
            //     render: (el) => (
            //         <Link to={{pathname: `${arc.path.base}vendor/${el.ARCIMEDES_ROW_ID}`}}><Icon svg={svgEdit}/></Link>
            //     ),
            // },
            {
                code: 'VENDOR_TYPE',
                name: 'Vendor Type',
                columnStyle: {
                    minWidth: 100
                },
                render: (el) => el.VENDOR_TYPE,
            },{
                code: 'NAME',
                name: 'Name',
                columnStyle: {
                    minWidth: 100,
                    whiteSpace: 'nowrap',
                },
                render: (el) => (
                    <Link to={{pathname: `${arc.path.base}vendor/${el.ARCIMEDES_ROW_ID}`}}>{el.NAME}</Link>
                ),
            },
            {
				code: 'PHONE',
				name: 'Cell Phone',
				columnStyle: {
					minWidth: 80,
					whiteSpace: 'nowrap',
				},
				render: (el) => phoneCell(el.PHONE),
			},
            {
				code: 'WORK_PHONE',
				name: 'Work Phone',
				columnStyle: {
					minWidth: 80,
					whiteSpace: 'nowrap',
				},
				render: (el) => phoneCell(el.WORK_PHONE),
			},
            {
				code: 'EMAIL_ID',
				name: 'Email',
                columnStyle: {
                    minWidth: 150
                },
				render: (el) => el.EMAIL_ID,
			},
            {
                code: 'LEAD_ID',
                name: 'Lead ID',
                columnStyle: {
                    whiteSpace: 'nowrap',
                },
                render: (el) => (
                    <OpenInCrmButton
                        leadId={el.LEAD_ID}
                        entityType="account"
                        variant="link"
                        label="Open in CRM"
                    />
                ),
            },
            {
                code: 'COMPANY_NAME',
                name: 'Firm Name',
                columnStyle: {
                    minWidth: 130,
                    whiteSpace: 'nowrap',
                },
                // The linked firm wins over the legacy free-text COMPANY_NAME:
                // CRM repoints FIRM_ID (bank mergers etc.) without touching the
                // text, and the vendor page shows the firm. (Claritask 547922)
                render: (el) => el.FIRM_NAME || el.COMPANY_NAME || null,
            },
            {
                code: 'COMPANY_ADDRESS',
                name: 'Address',
                columnStyle: {
                    minWidth: 230
                },
                render: (el) => el.COMPANY_ADDRESS,
            },
            {
                code: 'COMPANY_CITY',
                name: 'City',
                columnStyle: {
                    whiteSpace: 'nowrap',
                },
                render: (el) => el.COMPANY_CITY,
            },
            {
                code: 'COMPANY_STATE',
                name: 'State',
                columnStyle: {
                    minWidth: 80
                },
                render: (el) => el.COMPANY_STATE,
            },
            {
                code: 'COMPANY_ZIP',
                name: 'ZIP',
                columnStyle: {
                    minWidth: 80
                },
                render: (el) => el.COMPANY_ZIP,
            }
        ];

        return (
            <div className='VendorList' data-is-busy={this.state.isBusy}>
                <div className='page-rounded-corner-container'>
                    <div className='page-header-container list'>
                        <div className='page-header list' style={{flexDirection: 'column', alignItems: 'flex-start'}}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '10px', width: '100%'}}>
                                <div className='page-title'>Vendors</div>
                                <LinkComp to={`${arc.path.base}vendor`}>
                                    <div className="button-new plus positive">
                                        <Icon svg={plus}/>
                                    </div>
                                </LinkComp>
                                <button
                                    type="button"
                                    className="button-new text"
                                    onClick={() => this.setState({findDupsOpen: true})}
                                    title="Find vendors recorded more than once"
                                >
                                    Find Dups
                                </button>
                                {can('mail_merge') && (
                                    <button
                                        type="button"
                                        className={`button-new text ${this.state.preparingMailMerge ? 'inactive' : ''}`}
                                        onClick={this.startMailMerge}
                                        disabled={this.state.preparingMailMerge || this.state.totalCount === 0}
                                        title="Email every vendor this list matches"
                                    >
                                        {this.state.preparingMailMerge ? 'Preparing…' : 'Mail Merge'}
                                    </button>
                                )}
                            </div>
                            <FilterButton onClick={this.props.openFilter} />
                        </div>
                        <div className='line'/>
                    </div>
                </div>
                {this.state.mailMergeError ? <Alert type="error">{this.state.mailMergeError}</Alert> : null}
                <FindDuplicateContacts
                    open={this.state.findDupsOpen}
                    entity="vendors"
                    label="Vendor"
                    plural="Vendors"
                    route="vendor"
                    fetchFilteredSet={() => fetchFilteredIds(VENDOR_LIST, 'vendor', query)}
                    filteredCount={this.state.totalCount}
                    onClose={() => this.setState({findDupsOpen: false})}
                />
                <div className='page-body-container'>
                    <SortTable columns={columns}
                               items={this.state.items}
                               query={query}
                    />
                    <ListResults query={this.props.location.query}
                                 pathname={this.props.location.pathname}
                                 error={this.state.error}
                                 totalCount={this.state.totalCount}
                                 itemCount={this.state.items ? this.state.items.length : false}
                                 limit={limit}
                    />
                </div>
            </div>
        );
    }
}
